import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import RTPageLayout from '../common/RTPageLayout';

const toApiDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getDefaultFilters = () => {
  const today = new Date();
  return {
    from_date: new Date(today.getFullYear(), today.getMonth(), 1),
    to_date: today,
    categoryId: '',
    paymentMethod: '',
    staffId: '',
  };
};

const ViewExpenses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const filterFields = useMemo(
    () => [
      {
        key: 'from_date',
        label: 'Start Date',
        type: 'date',
        width: 'compact',
        applyImmediately: true,
      },
      {
        key: 'to_date',
        label: 'End Date',
        type: 'date',
        width: 'compact',
        applyImmediately: true,
      },
      {
        key: 'categoryId',
        label: 'Category',
        type: 'dropdown',
        width: 'medium',
        options: [
          { value: '', label: 'All Categories' },
          ...categories.map((c) => ({ value: String(c.id), label: c.name })),
        ],
      },
      {
        key: 'paymentMethod',
        label: 'Payment Method',
        type: 'dropdown',
        width: 'medium',
        options: [
          { value: '', label: 'All Methods' },
          ...paymentMethods.map((m) => ({ value: m.name, label: m.name })),
        ],
      },
      {
        key: 'staffId',
        label: 'Staff',
        type: 'dropdown',
        width: 'medium',
        options: [
          { value: '', label: 'All Staff' },
          ...staffOptions.map((s) => ({
            value: String(s.staff_id),
            label: s.name,
          })),
        ],
      },
    ],
    [categories, paymentMethods, staffOptions],
  );

  useEffect(() => {
    const abortController = new AbortController();

    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadCategories(abortController),
          loadPaymentMethods(abortController),
          loadStaffOptions(abortController),
          loadExpenses(appliedFilters, abortController, { isInitial: true }),
        ]);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading initial data:', error);
        }
      } finally {
        setHasLoadedOnce(true);
        setLoading(false);
      }
    };

    loadInitialData();
    return () => abortController.abort();
  }, []);

  const loadCategories = async (abortController = null) => {
    try {
      const response = await apiGet('/expenses/categories?status=all', {
        signal: abortController?.signal,
      });
      const data = response?.data || response || [];
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading categories:', error);
      }
    }
  };

  const loadPaymentMethods = async (abortController = null) => {
    try {
      const response = await apiGet('/payments/methods?status=active', {
        signal: abortController?.signal,
      });
      const data = response?.data || response || [];
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading payment methods:', error);
      }
    }
  };

  const loadStaffOptions = async (abortController = null) => {
    try {
      const response = await apiGet('/expenses/staff-options', {
        signal: abortController?.signal,
      });
      const data = response?.data || response || [];
      setStaffOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Staff options require add_expense; ignore if missing
        setStaffOptions([]);
      }
    }
  };

  const loadExpenses = async (
    filterParams = appliedFilters,
    abortController = null,
    { isInitial = false } = {},
  ) => {
    try {
      if (!isInitial) setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', '1000');
      const fromDate = toApiDate(filterParams.from_date);
      const toDate = toApiDate(filterParams.to_date);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (filterParams.categoryId)
        params.append('category_id', filterParams.categoryId);
      if (filterParams.paymentMethod) {
        params.append('payment_method', filterParams.paymentMethod);
      }
      if (filterParams.staffId) params.append('staff_id', filterParams.staffId);

      const response = await apiGet(`/expenses/?${params.toString()}`, {
        signal: abortController?.signal,
      });
      const apiResponse = response.data;

      if (apiResponse && apiResponse.success && apiResponse.data) {
        setExpenses(apiResponse.data);
        setTotalCount(apiResponse.total || apiResponse.data.length);
        setTotalAmount(apiResponse.total_amount || 0);
      } else {
        toastError(apiResponse?.message || 'Failed to load expenses');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading expenses:', error);
        toastError('Error loading expenses');
      }
    } finally {
      if (!isInitial) setLoading(false);
    }
  };

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = {
      ...filters,
      [key]:
        value instanceof Date || value === null || value === undefined
          ? value
          : (value ?? ''),
    };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      loadExpenses(next);
    }
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    loadExpenses(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.from_date) {
      pills.push({
        key: 'from_date',
        label: `Start: ${formatDateToDDMMYYYY(appliedFilters.from_date)}`,
      });
    }
    if (appliedFilters.to_date) {
      pills.push({
        key: 'to_date',
        label: `End: ${formatDateToDDMMYYYY(appliedFilters.to_date)}`,
      });
    }
    if (appliedFilters.categoryId) {
      const cat = categories.find(
        (c) => String(c.id) === String(appliedFilters.categoryId),
      );
      pills.push({
        key: 'categoryId',
        label: `Category: ${cat?.name || appliedFilters.categoryId}`,
      });
    }
    if (appliedFilters.paymentMethod) {
      pills.push({
        key: 'paymentMethod',
        label: `Payment Method: ${appliedFilters.paymentMethod}`,
      });
    }
    if (appliedFilters.staffId) {
      const staff = staffOptions.find(
        (s) => String(s.staff_id) === String(appliedFilters.staffId),
      );
      pills.push({
        key: 'staffId',
        label: `Staff: ${staff?.name || appliedFilters.staffId}`,
      });
    }
    return pills;
  }, [appliedFilters, categories, staffOptions]);

  const handleRemoveAppliedFilter = (key) => {
    const defaultFilters = getDefaultFilters();
    const next = { ...appliedFilters };
    if (key === 'from_date') next.from_date = defaultFilters.from_date;
    else if (key === 'to_date') next.to_date = defaultFilters.to_date;
    else next[key] = '';
    setFilters(next);
    setAppliedFilters(next);
    loadExpenses(next);
  };

  const formatAmount = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN');

  const getTableTitle = () => {
    let title = 'Expenses';
    if (appliedFilters.from_date && appliedFilters.to_date) {
      title += ` - ${formatDateToDDMMYYYY(appliedFilters.from_date)} to ${formatDateToDDMMYYYY(appliedFilters.to_date)}`;
    }
    title += ` (${totalCount} record${totalCount !== 1 ? 's' : ''})`;
    return title;
  };

  const columns = [
    {
      Header: 'Date',
      accessor: 'expense_date',
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: 'Category',
      accessor: 'category_name',
    },
    {
      Header: 'Staff',
      accessor: 'staff_name',
      Cell: ({ value }) => value || '—',
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ value }) => formatAmount(value),
    },
    {
      Header: 'Payment Method',
      accessor: 'payment_method',
    },
    {
      Header: 'Description',
      accessor: 'notes',
      Cell: ({ value }) => value || '—',
    },
  ];

  return (
    <RTPageLayout activePath={location.pathname} title="View Expenses">
      {!hasLoadedOnce ? (
        <div
          className="rt-main-section"
          style={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RTSpinner message="Loading expenses..." fullScreen={false} />
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '0.75rem',
              marginBottom: '0.75rem',
            }}
          >
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #153C7F',
                borderRight: '4px solid #153C7F',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Total Expenses
              </div>
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: '#153C7F',
                }}
              >
                {totalCount}
              </div>
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #153C7F',
                borderRight: '4px solid #153C7F',
                borderRadius: '8px',
                padding: '0.4rem 0.8rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Total Amount
              </div>
              <div
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#153C7F',
                }}
              >
                {formatAmount(totalAmount)}
              </div>
            </div>
          </div>

          <div className="rt-main-section">
            <RTFilteredTable
              loading={loading}
              loadingMessage="Loading expenses..."
              filters={
                <RTFilters
                  fields={filterFields}
                  values={filters}
                  onChange={handleFilterChange}
                  pills={appliedFilterPills}
                  onRemovePill={handleRemoveAppliedFilter}
                  onClearAll={handleClearFilters}
                />
              }
            >
              <Table
                columns={columns}
                data={expenses}
                title={getTableTitle()}
                showSearch={true}
                exportable={true}
                exportFileName="expenses"
                showPagination={true}
                noDataText="No expenses found for the selected criteria"
              />
            </RTFilteredTable>
          </div>
        </>
      )}
    </RTPageLayout>
  );
};

export default ViewExpenses;
